import { Grid, Typography } from '@mui/material'
import { v4 as getRandomId } from 'uuid'
import { Autocomplete } from 'components/Autocomplete/Autocomplete'
import { useFormikContext } from 'formik'
import { useCodeList } from 'hooks/useCodeList'
import AddIcon from '@mui/icons-material/Add'
import { t } from 'i18next'
import theme from 'lib/theme'
import { Fragment, useState } from 'react'
import { MandatoryNutrientDetails, NutrientDetail } from 'types/model/product'
import { MandatoryNutrients } from './MandatoryNutrients'
import { OptionalNutrients } from './OptionalNutrients'
import { AddButtonWrapper } from './Product.styled'

type ProductNutrientsType = {
  measurementUnitCodes: { value: string; label: string }[]
  mandatoryNutrientDetails: MandatoryNutrientDetails
  nutrientDetails: NutrientDetail[]
  index: number
}

export type NutrientFieldType = {
  name: string
  translatedName: string
  amountInputName?: string
  unit?: string
}

// mandatory nutrients
const nutrientFields: NutrientFieldType[] = [
  {
    name: 'energy',
    translatedName: t('energy'),
    amountInputName: 'kjQuantity',
    unit: 'KJ',
  },
  {
    name: 'energy',
    translatedName: t('energy'),
    amountInputName: 'kCalQuantity',
    unit: 'kCal',
  },
  {
    name: 'fat',
    translatedName: t('fat'),
  },
  {
    name: 'saturatedFat',
    translatedName: t('ofWhichSaturates'),
  },
  {
    name: 'carbohydrate',
    translatedName: t('carbohydrates'),
  },
  {
    name: 'sugar',
    translatedName: t('ofWhichSugars'),
  },
  {
    name: 'fiber',
    translatedName: t('fiber'),
  },
  {
    name: 'protein',
    translatedName: t('protein'),
  },
  {
    name: 'salt',
    translatedName: t('salt'),
  },
]

export const ProductNutrients = ({
  measurementUnitCodes,
  mandatoryNutrientDetails,
  nutrientDetails,
  index,
}: ProductNutrientsType) => {
  // translated measurement labels unnecessary
  const measurementCodes = measurementUnitCodes.map((code) => {
    return { label: code.value, value: code.value }
  })
  const { codeListTypes: nutrientTypeCodes } = useCodeList(
    'nutrientTypeCode',
    true
  )
  const [newNutrient, setNewNutrient] = useState<{
    label: string
    value: string
  } | null>()
  const { setFieldValue } = useFormikContext()

  const handleAdd = () => {
    if (newNutrient) {
      const newNutrientDetails = [
        ...nutrientDetails,
        {
          nutrientTypeCode: newNutrient.value,
          id: getRandomId(),
          quantity: '',
          dailyValueIntakePercent: '',
        },
      ]
      setFieldValue(
        `nutrientHeaders.${index}.nutrientDetails`,
        newNutrientDetails
      )
      setNewNutrient(null)
    }
  }

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          paddingBottom: theme.spacing(1),
          borderBottom: `1px solid ${theme.palette.action.disabled}`,
          marginBottom: theme.spacing(2),
        }}>
        <Grid item xs={3.5}>
          <Typography
            variant='body2'
            sx={{
              color: theme.palette.text.secondary,
            }}>
            {t('nutrient').toUpperCase()}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            variant='body2'
            sx={{ color: theme.palette.text.secondary }}>
            {t('amount').toUpperCase()}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            variant='body2'
            sx={{ color: theme.palette.text.secondary }}>
            {t('unit').toUpperCase()}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            variant='body2'
            sx={{ color: theme.palette.text.secondary }}>
            {t('dailyIntake').toUpperCase()}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        spacing={1}
        container
        sx={{
          marginBottom: theme.spacing(6),
        }}>
        {nutrientFields.map((field) => (
          <MandatoryNutrients
            key={field.name + field.unit}
            field={field}
            mandatoryNutrientDetails={mandatoryNutrientDetails}
            index={index}
            measurementCodes={measurementCodes}
          />
        ))}
        {nutrientDetails?.map((nutrient, i) => (
          <OptionalNutrients
            key={nutrient.id}
            nutrient={nutrient}
            index={index}
            i={i}
            measurementCodes={measurementCodes}
            nutrientTypeCodes={nutrientTypeCodes}
            nutrientDetails={nutrientDetails}
          />
        ))}
        <Grid item xs={10}>
          <Autocomplete
            options={nutrientTypeCodes ?? []}
            onChange={(newUnit) => {
              setNewNutrient(newUnit)
            }}
            onClear={() => setNewNutrient(null)}
            value={newNutrient || null}
            name='add'
            label=''
            placeholder={t('add')}
          />
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <AddButtonWrapper
            aria-label={t('addCategory')}
            onClick={handleAdd}
            disabled={!newNutrient}>
            <AddIcon
              sx={{
                fill: theme.palette.commonColors.white,
                fontWeight: 'bold',
                '&:hover': {
                  fill: theme.palette.commonColors.white,
                },
              }}
            />
          </AddButtonWrapper>
        </Grid>
      </Grid>
    </>
  )
}
